import { Component, OnInit, Input, OnChanges , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { WeekPlanTaskService, WeekPlanTask, CreateWeekPlanTaskDto } from '../../services/week-plan-task.service';

@Component({
  selector: 'app-current-week-plan',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Tag,
    Tooltip,
    Dialog,
    InputText,
    Select,
    DatePicker,
    ConfirmDialog,
  ],
  templateUrl: './current-week-plan.component.html',
  styleUrl: './current-week-plan.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class CurrentWeekPlanComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  @Input() projectId: string = '';

  tasks: WeekPlanTask[] = [];
  selectedTask: WeekPlanTask | null = null;
  displayTaskDialog: boolean = false;
  isEditTaskMode: boolean = false;
  taskStartDate: Date | null = null;
  taskTargetDate: Date | null = null;
  loading: boolean = false;
  fieldTouched: Record<string, boolean> = {};

  taskStatusOptions = [
    { label: 'On Track', value: 'On Track' },
    { label: 'On Hold', value: 'On Hold' },
    { label: 'Delayed', value: 'Delayed' },
    { label: 'Completed', value: 'Completed' }
  ];

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private weekPlanTaskService: WeekPlanTaskService
  ) {}

  ngOnInit() {
    if (this.projectId) {
      this.loadTasks();
    }
  }

  ngOnChanges() {
    if (this.projectId) {
      this.loadTasks();
    }
  }

  loadTasks() {
    if (!this.projectId) {
      return;
    }
    this.loading = true;
    this.weekPlanTaskService.getByProjectId(this.projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (tasks) => {
        this.tasks = tasks;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading tasks:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load tasks. Please try again.'
        });
        this.loading = false;
      }
    });
  }

  showAddTaskDialog() {
    if (!this.projectId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Project ID is required to add tasks.'
      });
      return;
    }
    this.selectedTask = {
      id: '',
      projectId: this.projectId,
      taskNumber: this.generateTaskNumber(),
      task: '',
      startDate: new Date().toISOString().split('T')[0],
      targetDate: '',
      status: 'On Track',
      comments: ''
    };
    this.taskStartDate = new Date();
    this.taskTargetDate = null;
    this.fieldTouched = {};
    this.isEditTaskMode = false;
    this.displayTaskDialog = true;
  }

  showEditTaskDialog(task: WeekPlanTask) {
    this.selectedTask = { ...task };
    this.taskStartDate = task.startDate ? new Date(task.startDate) : null;
    this.taskTargetDate = task.targetDate ? new Date(task.targetDate) : null;
    this.fieldTouched = {};
    this.isEditTaskMode = true;
    this.displayTaskDialog = true;
  }

  resetTaskForm() {
    this.fieldTouched = {};
    if (!this.selectedTask) return;
    if (this.isEditTaskMode) {
      const task = this.tasks.find(t => t.id === this.selectedTask?.id);
      if (task) {
        this.selectedTask = { ...task };
        this.taskStartDate = task.startDate ? new Date(task.startDate) : null;
        this.taskTargetDate = task.targetDate ? new Date(task.targetDate) : null;
      }
    } else {
      this.selectedTask.taskNumber = this.generateTaskNumber();
      this.selectedTask.task = '';
      this.selectedTask.startDate = new Date().toISOString().split('T')[0];
      this.selectedTask.targetDate = '';
      this.selectedTask.status = 'To Start';
      this.selectedTask.comments = '';
      this.taskStartDate = new Date();
      this.taskTargetDate = null;
    }
  }

  saveTask() {
    this.fieldTouched['task'] = true;

    if (!this.selectedTask || !this.selectedTask.task.trim()) {
      return;
    }

    if (!this.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Project ID is required.'
      });
      return;
    }

    if (this.taskStartDate) {
      this.selectedTask.startDate = this.taskStartDate.toISOString().split('T')[0];
    }

    if (this.taskTargetDate) {
      this.selectedTask.targetDate = this.taskTargetDate.toISOString().split('T')[0];
    } else {
      this.selectedTask.targetDate = undefined;
    }

    if (this.isEditTaskMode) {
      // Update existing task
      this.weekPlanTaskService.updateTask(this.selectedTask.id, this.selectedTask).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updatedTask) => {
          const index = this.tasks.findIndex(t => t.id === updatedTask.id);
          if (index !== -1) {
            this.tasks[index] = updatedTask;
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Task updated successfully.'
          });
          this.displayTaskDialog = false;
          this.selectedTask = null;
        },
        error: (error) => {
          console.error('Error updating task:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update task. Please try again.'
          });
        }
      });
    } else {
      // Create new task
      const createDto: CreateWeekPlanTaskDto = {
        projectId: this.projectId,
        taskNumber: this.selectedTask.taskNumber,
        task: this.selectedTask.task,
        startDate: this.selectedTask.startDate,
        targetDate: this.selectedTask.targetDate,
        status: this.selectedTask.status,
        comments: this.selectedTask.comments
      };

      this.weekPlanTaskService.addTask(createDto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (newTask) => {
          this.tasks.push(newTask);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Task added successfully.'
          });
          this.displayTaskDialog = false;
          this.selectedTask = null;
        },
        error: (error) => {
          console.error('Error adding task:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add task. Please try again.'
          });
        }
      });
    }
  }

  deleteTask(task: WeekPlanTask) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${task.task || ''}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.weekPlanTaskService.deleteTask(task.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            const index = this.tasks.findIndex(t => t.id === task.id);
            if (index !== -1) {
              this.tasks.splice(index, 1);
            }
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Task deleted successfully.'
            });
          },
          error: (error) => {
            console.error('Error deleting task:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete task. Please try again.'
            });
          }
        });
      }
    });
  }

  generateTaskNumber(): string {
    if (this.tasks.length === 0) {
      return '00001';
    }
    const maxId = Math.max(...this.tasks.map(t => {
      const num = parseInt(t.taskNumber.replace(/\D/g, ''));
      return isNaN(num) ? 0 : num;
    }));
    return String(maxId + 1).padStart(5, '0');
  }

  formatTaskDate(dateString?: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  }

  getTaskStatusSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'On Track':
      case 'Completed':
        return 'success';
      case 'On Hold':
        return 'warn';
      case 'Delayed':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  moveTaskUp(task: WeekPlanTask) {
    const index = this.tasks.indexOf(task);
    if (index > 0) {
      // Swap tasks
      [this.tasks[index - 1], this.tasks[index]] = [this.tasks[index], this.tasks[index - 1]];
      // Update task numbers
      this.updateTaskNumbers();
      // Save the changes
      this.saveTaskOrder();
    }
  }

  moveTaskDown(task: WeekPlanTask) {
    const index = this.tasks.indexOf(task);
    if (index < this.tasks.length - 1) {
      // Swap tasks
      [this.tasks[index], this.tasks[index + 1]] = [this.tasks[index + 1], this.tasks[index]];
      // Update task numbers
      this.updateTaskNumbers();
      // Save the changes
      this.saveTaskOrder();
    }
  }

  updateTaskNumbers() {
    this.tasks.forEach((task, index) => {
      const newNumber = String(index + 1).padStart(5, '0');
      if (task.taskNumber !== newNumber) {
        task.taskNumber = newNumber;
      }
    });
  }

  saveTaskOrder() {
    // Update all tasks with new order and numbers
    const updatePromises = this.tasks.map((task, index) => {
      const updatedTask = {
        ...task,
        taskNumber: String(index + 1).padStart(5, '0')
      };
      return this.weekPlanTaskService.updateTask(task.id, updatedTask).toPromise();
    });

    Promise.all(updatePromises).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Task order updated successfully.'
      });
    }).catch((error) => {
      console.error('Error updating task order:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update task order. Please try again.'
      });
      // Reload tasks to restore original order
      this.loadTasks();
    });
  }
}
